import { isMoneyString } from "@ibada/shared-types/finance"

import { http } from "@/shared/lib/api/http"
import {
  type CancelPledgeInput,
  type CreatePledgeInput,
  type ListPledgesQuery,
  type ListPledgesResponse,
  type PledgeCampaign,
  type PledgeCampaignsResponse,
  type PledgeDetailResponse,
  type PledgeMemberSummary,
  type PledgePaymentHistoryItem,
  type PledgeRecord,
  type RecordInKindFulfillmentInput,
  PledgeContributorType,
  PledgeCampaignType,
  PledgeKind,
  PledgeListView,
  PledgeStatus,
} from "@/features/pledges/model/pledge-types"
import { type ActivityMemberSummary } from "@/features/finance-activities/model/activities-types"

export type ListPledgesTableQuery = ListPledgesQuery & {
  source?: string
  search?: string
  sortBy?: "pledgedAt" | "dueAt" | "createdAt" | "amountPledged" | "amountReceived" | "status" | "lateFirst"
  sortOrder?: "asc" | "desc"
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null
  return normalizeString(value) ?? null
}

function normalizeNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return 0
}

/**
 * A pledge figure, read as the exact decimal STRING the API sent (IBA-116).
 *
 * `normalizeNumber` is what these fields used to run through, and it is precisely the doorway this ticket closes:
 * `Number("9007199254740993.01")` is `9007199254740992`, and a dollar disappears from a large pledge with no error
 * anywhere. The string is passed through untouched — `formatCurrency`/`Intl` parse it exactly, so nothing in the
 * browser ever needs it as a float.
 *
 * A legacy `number` is still tolerated (a cached response from before this change) and rendered via its decimal
 * representation, which is the most that can be recovered from a value that was already floated upstream.
 */
function normalizeAmountString(value: unknown, fallback: string): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (isMoneyString(trimmed)) return trimmed
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return fallback
}

function normalizeNullableAmountString(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === "string" && value.trim().length === 0) return null
  const normalized = normalizeAmountString(value, "")
  return normalized.length > 0 ? normalized : null
}

function normalizePledgeStatus(value: unknown): PledgeStatus {
  if (value === PledgeStatus.PartiallyFulfilled) return PledgeStatus.PartiallyFulfilled
  if (value === PledgeStatus.Fulfilled) return PledgeStatus.Fulfilled
  if (value === PledgeStatus.Cancelled) return PledgeStatus.Cancelled
  if (value === PledgeStatus.Overdue) return PledgeStatus.Overdue
  // Awaiting review (a form submission): read as OPEN it looked payable and late, which it is neither.
  if (value === PledgeStatus.Pending) return PledgeStatus.Pending
  return PledgeStatus.Open
}

function normalizeContributorType(value: unknown): PledgeContributorType {
  if (value === PledgeContributorType.Group) return PledgeContributorType.Group
  if (value === PledgeContributorType.Ministry) return PledgeContributorType.Ministry
  if (value === PledgeContributorType.Member) return PledgeContributorType.Member
  return PledgeContributorType.Member
}

function normalizeMemberSummary(input: unknown): ActivityMemberSummary | null {
  if (!input || typeof input !== "object") return null
  const record = input as Record<string, unknown>
  const id = normalizeString(record.id)
  const firstName = normalizeString(record.firstName ?? record.first_name)
  const lastName = normalizeString(record.lastName ?? record.last_name)
  if (!id || !firstName || !lastName) return null

  return {
    id,
    firstName,
    middleName: normalizeNullableString(record.middleName ?? record.middle_name),
    lastName,
  }
}

/** The pledge's own member, with the contact the server only sends to a reader who may read members. */
function normalizePledgeMember(input: unknown): PledgeMemberSummary | null {
  const member = normalizeMemberSummary(input)
  if (!member) return null
  const record = input as Record<string, unknown>
  return {
    ...member,
    phone: normalizeNullableString(record.phone),
    avatarUrl: normalizeNullableString(record.avatarUrl ?? record.avatar_url),
  }
}

function normalizeUserSummary(input: unknown): PledgeRecord["createdByUser"] {
  if (!input || typeof input !== "object") return null
  const record = input as Record<string, unknown>
  const id = normalizeString(record.id)
  const email = normalizeString(record.email)
  const firstName = normalizeString(record.firstName ?? record.first_name)
  const lastName = normalizeString(record.lastName ?? record.last_name)
  if (!id || !email || !firstName || !lastName) return null

  return {
    id,
    email,
    firstName,
    lastName,
    avatarUrl: normalizeNullableString(record.avatarUrl ?? record.avatar_url),
  }
}

function normalizeNamedRecord(value: unknown) {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  const slug = normalizeString(record.slug)
  const status = normalizeString(record.status)
  const branchType = normalizeNullableString(record.branchType ?? record.branch_type)

  if (!id || !name) return null

  return {
    id,
    name,
    slug: slug ?? "",
    status: status ?? "",
    branchType: branchType === "SYSTEM" || branchType === "NORMAL" ? branchType : null,
  }
}

function normalizeOrganizationUnit(value: unknown) {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  const status = normalizeString(record.status)

  if (!id || !name) return null

  return {
    id,
    name,
    status: status ?? "",
  }
}

function normalizeContributorSummary(value: unknown) {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const type = normalizeContributorType(record.type)
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  if (!id || !name) return null

  return { type, id, name }
}

function normalizeWorkspace(value: unknown): PledgeRecord["workspace"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  const displayName = normalizeString(record.displayName ?? record.display_name)
  const slug = normalizeString(record.slug)
  const status = normalizeString(record.status)

  if (!id || !name || !displayName || !slug || !status) return null

  return { id, name, displayName, slug, status }
}

function normalizeActivity(value: unknown): PledgeRecord["activity"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const title = normalizeString(record.title)

  if (!id || !title) return null

  return {
    id,
    title,
    status: normalizeString(record.status) ?? "",
    startsAt: normalizeNullableString(record.startsAt ?? record.starts_at),
    endsAt: normalizeNullableString(record.endsAt ?? record.ends_at),
  }
}

function normalizeFund(value: unknown): PledgeRecord["fund"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)

  if (!id || !name) return null

  return {
    id,
    name: name,
    nameTranslationKey: normalizeNullableString(record.nameTranslationKey ?? record.name_translation_key),
  }
}

function normalizePledge(input: unknown): PledgeRecord {
  const record = (input ?? {}) as Record<string, unknown>
  const contributorType = normalizeContributorType(record.contributorType ?? record.contributor_type)
  const member = normalizePledgeMember(record.member)
  const group = normalizeOrganizationUnit(record.group)
  const ministry = normalizeOrganizationUnit(record.ministry)
  const derivedContributor =
    contributorType === PledgeContributorType.Group && group
      ? { type: PledgeContributorType.Group, id: group.id, name: group.name }
      : contributorType === PledgeContributorType.Ministry && ministry
        ? { type: PledgeContributorType.Ministry, id: ministry.id, name: ministry.name }
        : contributorType === PledgeContributorType.Member && member
          ? {
              type: PledgeContributorType.Member,
              id: member.id,
              name: [member.firstName, member.middleName, member.lastName].filter(Boolean).join(" "),
            }
          : null

  return {
    id: normalizeString(record.id) ?? "",
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? "",
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? "",
    activityId: normalizeNullableString(record.activityId ?? record.activity_id),
    operationalActivityId: normalizeNullableString(
      record.operationalActivityId ?? record.operational_activity_id,
    ),
    fundId: normalizeNullableString(record.fundId ?? record.fund_id),
    contributorType,
    memberId: normalizeNullableString(record.memberId ?? record.member_id),
    groupId: normalizeNullableString(record.groupId ?? record.group_id),
    ministryId: normalizeNullableString(record.ministryId ?? record.ministry_id),
    pledgerName: normalizeNullableString(record.pledgerName ?? record.pledger_name),
    pledgerEmail: normalizeNullableString(record.pledgerEmail ?? record.pledger_email),
    pledgerPhone: normalizeNullableString(record.pledgerPhone ?? record.pledger_phone),
    kind: (record.kind === "IN_KIND" ? PledgeKind.InKind : PledgeKind.Money) as PledgeKind,
    inKindDescription: normalizeNullableString(record.inKindDescription ?? record.in_kind_description),
    inKindUnit: normalizeNullableString(record.inKindUnit ?? record.in_kind_unit),
    estimatedValue: normalizeNullableAmountString(record.estimatedValue ?? record.estimated_value),
    estimatedValueCurrency: normalizeNullableString(record.estimatedValueCurrency ?? record.estimated_value_currency),
    // The server states the currency its money figures are in — never fall back to a hardcoded code.
    currency: normalizeString(record.currency) ?? "",
    amountPledged: normalizeAmountString(record.amountPledged ?? record.amount_pledged, "0"),
    amountReceived: normalizeAmountString(record.amountReceived ?? record.amount_received, "0"),
    remainingAmount: normalizeAmountString(record.remainingAmount ?? record.remaining_amount, "0"),
    // A ratio, not money — and the server clamps it, so the client renders it as given.
    fulfillmentPercentage: normalizeNumber(record.fulfillmentPercentage ?? record.fulfillment_percentage),
    status: normalizePledgeStatus(record.status),
    pledgedAt: normalizeNullableString(record.pledgedAt ?? record.pledged_at),
    dueAt: normalizeNullableString(record.dueAt ?? record.due_at),
    notes: normalizeNullableString(record.notes),
    createdByMemberId: normalizeNullableString(record.createdByMemberId ?? record.created_by_member_id),
    createdByUserId: normalizeNullableString(record.createdByUserId ?? record.created_by_user_id),
    source: normalizeNullableString(record.source),
    contributor: normalizeContributorSummary(record.contributor) ?? derivedContributor,
    workspace: normalizeWorkspace(record.workspace),
    branch: normalizeNamedRecord(record.branch),
    activity: normalizeActivity(record.activity),
    fund: normalizeFund(record.fund),
    member,
    group,
    ministry,
    createdByMember: normalizeMemberSummary(record.createdByMember ?? record.created_by_member),
    createdByUser: normalizeUserSummary(record.createdByUser ?? record.created_by_user),
    createdAt: normalizeNullableString(record.createdAt ?? record.created_at),
    updatedAt: normalizeNullableString(record.updatedAt ?? record.updated_at),
  }
}

function normalizePaymentHistoryItem(input: unknown): PledgePaymentHistoryItem {
  const record = (input ?? {}) as Record<string, unknown>
  return {
    transactionId: normalizeString(record.transactionId ?? record.transaction_id) ?? "",
    transactionReference: normalizeNullableString(record.transactionReference ?? record.transaction_reference),
    amount: normalizeAmountString(record.amount, "0"),
    occurredAt: normalizeNullableString(record.occurredAt ?? record.occurred_at),
    description: normalizeNullableString(record.description),
    status: record.status === "VOIDED" ? "VOIDED" : "ACTIVE",
    type: record.type === "EXPENSE" ? "EXPENSE" : "INCOME",
  }
}

export function buildListParams(query: ListPledgesTableQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    workspaceId: query.workspaceId,
  }

  if (query.branchId) params.branchId = query.branchId
  if (query.activityId) params.activityId = query.activityId
  if (query.fundId) params.fundId = query.fundId
  if (query.memberId) params.memberId = query.memberId
  if (query.groupId) params.groupId = query.groupId
  if (query.ministryId) params.ministryId = query.ministryId
  if (query.contributorType) params.contributorType = query.contributorType
  if (query.kind) params.kind = query.kind
  if (query.fulfillment) params.fulfillment = query.fulfillment
  if (query.status) params.status = query.status
  if (query.view) params.view = query.view
  if (query.campaign) params.campaign = query.campaign
  if (query.source) params.source = query.source
  if (query.pledgedAtFrom) params.pledgedAtFrom = query.pledgedAtFrom
  if (query.pledgedAtTo) params.pledgedAtTo = query.pledgedAtTo
  if (query.dueAtFrom) params.dueAtFrom = query.dueAtFrom
  if (query.dueAtTo) params.dueAtTo = query.dueAtTo
  if (query.search) params.search = query.search
  if (typeof query.page === "number" && query.page > 0) params.page = query.page
  if (typeof query.limit === "number" && query.limit > 0) params.limit = Math.min(query.limit, 100)
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder

  return params
}

export async function listPledges(query: ListPledgesTableQuery): Promise<ListPledgesResponse> {
  const response = await http.get<unknown>("/finance/pledges", {
    params: buildListParams(query),
  })

  const record = (response.data ?? {}) as Record<string, unknown>
  const items = Array.isArray(record.items) ? (record.items as unknown[]).map(normalizePledge) : []
  const summary = (record.summary ?? {}) as Record<string, unknown>

  return {
    items,
    total: normalizeNumber(record.total),
    page: normalizeNumber(record.page) || 1,
    limit: normalizeNumber(record.limit) || Math.max(items.length, 1),
    summary: {
      // Totals and their currency come from the server, summed as decimals. Nothing here is added in a browser.
      currency: normalizeString(summary.currency) ?? "",
      totalPledged: normalizeAmountString(summary.totalPledged ?? summary.total_pledged, "0"),
      totalReceived: normalizeAmountString(summary.totalReceived ?? summary.total_received, "0"),
      totalRemaining: normalizeAmountString(summary.totalRemaining ?? summary.total_remaining, "0"),
      fulfillmentPercentage: normalizeNumber(summary.fulfillmentPercentage ?? summary.fulfillment_percentage),
      count: normalizeNumber(summary.count),
      inKindCount: normalizeNumber(summary.inKindCount ?? summary.in_kind_count),
      inKindEstimatedValue: normalizeAmountString(summary.inKindEstimatedValue ?? summary.in_kind_estimated_value, "0"),
      inKindEstimatedValueReceived: normalizeAmountString(
        summary.inKindEstimatedValueReceived ?? summary.in_kind_estimated_value_received,
        "0",
      ),
    },
  }
}

export async function getPledgeDetail(id: string): Promise<PledgeDetailResponse> {
  const response = await http.get<unknown>(`/finance/pledges/${id}`)
  const record = (response.data ?? {}) as Record<string, unknown>
  const paymentHistory = Array.isArray(record.paymentHistory ?? record.payment_history)
    ? ((record.paymentHistory ?? record.payment_history) as unknown[]).map(normalizePaymentHistoryItem)
    : []

  return {
    pledge: normalizePledge(record.pledge ?? record),
    paymentHistory,
  }
}

export async function createPledge(input: CreatePledgeInput): Promise<PledgeRecord> {
  const response = await http.post<unknown>("/finance/pledges", {
    workspaceId: input.workspaceId,
    branchId: input.branchId,
    activityId: input.activityId ?? null,
    fundId: input.fundId,
    contributorType: input.contributorType,
    memberId: input.memberId ?? null,
    groupId: input.groupId ?? null,
    ministryId: input.ministryId ?? null,
    pledgerName: input.pledgerName ?? null,
    pledgerEmail: input.pledgerEmail ?? null,
    pledgerPhone: input.pledgerPhone ?? null,
    kind: input.kind ?? PledgeKind.Money,
    inKindDescription: input.inKindDescription ?? null,
    inKindUnit: input.inKindUnit ?? null,
    // Decimal strings, sent through untouched (IBA-116). `""` is "not stated", which is not a zero.
    estimatedValue: input.estimatedValue ? input.estimatedValue : null,
    estimatedValueCurrency: input.estimatedValueCurrency ?? null,
    amountPledged: input.amountPledged,
    pledgedAt: input.pledgedAt,
    dueAt: input.dueAt ?? null,
    notes: input.notes ?? null,
    createdByMemberId: input.createdByMemberId ?? null,
    source: input.source ?? "MANUAL",
  })

  return normalizePledge(response.data)
}

export async function cancelPledge(input: CancelPledgeInput): Promise<PledgeRecord> {
  const response = await http.post<unknown>(`/finance/pledges/${input.id}/cancel`, {
    reason: input.reason ?? null,
  })

  return normalizePledge(response.data)
}

// Approve a PENDING (Form Purpose) pledge → accept it into the normal lifecycle (PENDING → OPEN).
export async function approvePledge(id: string): Promise<PledgeRecord> {
  const response = await http.post<unknown>(`/finance/pledges/${id}/approve`, {})
  return normalizePledge(response.data)
}

// Decline a PENDING (Form Purpose) pledge → CANCELLED, with an optional reason.
export async function declinePledge(input: { id: string; reason?: string | null }): Promise<PledgeRecord> {
  const response = await http.post<unknown>(`/finance/pledges/${input.id}/decline`, { reason: input.reason ?? null })
  return normalizePledge(response.data)
}

// Record a in-kind delivery (quantity received). This hits a dedicated endpoint that ONLY increments
// the received quantity and recomputes status — it never creates a finance transaction or moves money.
export async function recordInKindFulfillment(
  input: RecordInKindFulfillmentInput,
): Promise<PledgeRecord> {
  const response = await http.post<unknown>(`/finance/pledges/${input.id}/in-kind-fulfillments`, {
    quantityReceived: input.quantityReceived,
    occurredAt: input.occurredAt,
    note: input.note ?? null,
  })

  return normalizePledge(response.data)
}

function normalizeListView(value: unknown, fallback: PledgeListView): PledgeListView {
  return Object.values(PledgeListView).includes(value as PledgeListView) ? (value as PledgeListView) : fallback
}

function normalizeCampaign(input: unknown): PledgeCampaign {
  const record = (input ?? {}) as Record<string, unknown>
  const type = Object.values(PledgeCampaignType).includes(record.type as PledgeCampaignType)
    ? (record.type as PledgeCampaignType)
    : PledgeCampaignType.Other
  const money = record.money && typeof record.money === "object" ? (record.money as Record<string, unknown>) : null
  const inKind = Array.isArray(record.inKind) ? (record.inKind as Record<string, unknown>[]) : []
  return {
    key: normalizeString(record.key) ?? "other",
    type,
    name: normalizeNullableString(record.name),
    nameTranslationKey: normalizeNullableString(record.nameTranslationKey),
    startsAt: normalizeNullableString(record.startsAt),
    endsAt: normalizeNullableString(record.endsAt),
    pledgeCount: normalizeNumber(record.pledgeCount),
    contributorCount: normalizeNumber(record.contributorCount),
    lateCount: normalizeNumber(record.lateCount),
    money: money
      ? {
          pledged: normalizeAmountString(money.pledged, "0"),
          received: normalizeAmountString(money.received, "0"),
          fulfillmentPercentage: normalizeNumber(money.fulfillmentPercentage),
        }
      : null,
    inKind: inKind.map((line) => ({
      description: normalizeString(line.description) ?? "",
      unit: normalizeNullableString(line.unit),
      pledged: normalizeAmountString(line.pledged, "0"),
      received: normalizeAmountString(line.received, "0"),
      fulfillmentPercentage: normalizeNumber(line.fulfillmentPercentage),
    })),
    inKindEstimatedValue: normalizeNullableAmountString(record.inKindEstimatedValue),
    inKindEstimatedValueReceived: normalizeNullableAmountString(record.inKindEstimatedValueReceived),
  }
}

/** The campaign cards and the tab counts of the pledges page, in one read. */
export async function listPledgeCampaigns(query: {
  workspaceId: string
  branchId: string
  view: PledgeListView
}): Promise<PledgeCampaignsResponse> {
  const response = await http.get<unknown>("/finance/pledges/campaigns", { params: query })
  const record = (response.data ?? {}) as Record<string, unknown>
  const counts = (record.counts ?? {}) as Record<string, unknown>
  return {
    currency: normalizeString(record.currency) ?? "",
    view: normalizeListView(record.view, query.view),
    counts: {
      [PledgeListView.InProgress]: normalizeNumber(counts[PledgeListView.InProgress]),
      [PledgeListView.Late]: normalizeNumber(counts[PledgeListView.Late]),
      [PledgeListView.Fulfilled]: normalizeNumber(counts[PledgeListView.Fulfilled]),
      [PledgeListView.All]: normalizeNumber(counts[PledgeListView.All]),
    },
    campaigns: Array.isArray(record.campaigns) ? (record.campaigns as unknown[]).map(normalizeCampaign) : [],
  }
}
